/**
 * Guide (tutorial) composable - central singleton state for the guide system.
 *
 * Flow for grouped tours ("guide mode"):
 *   startTutorial(stepId) -> fullscreen intro splash (auto-dismiss) -> step 1 -> ... -> done
 * Single tips (no group) skip the splash and show the tooltip directly.
 *
 * Completing a tour, or leaving it via "skip all", marks every step in the
 * group as seen for the current user (localStorage, per-user).
 */

import { ref, computed, readonly } from 'vue'
import { isSeenByUser, markSeenByUser } from './tutorialStorage'
import { useCartStore } from '../../app/store/cart.js'

// Tutorials in this group are the only ones allowed while the cart drawer is open
const CART_TUTORIAL_GROUP = 'cart-intro'

// How long the fullscreen intro splash stays up before the tour begins
export const INTRO_SPLASH_MS = 1800

/**
 * Importance level configuration (chip label + colors)
 */
export const IMPORTANCE_LEVELS = {
  info: {
    label: 'כדאי לדעת',
    color: '#3b82f6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: '#3b82f6',
  },
  important: {
    label: 'חשוב לדעת',
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    borderColor: '#f59e0b',
  },
  critical: {
    label: 'קריטי לדעת',
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: '#ef4444',
  },
}

// ============================================
// Singleton state (shared across all callers)
// ============================================
const registeredElements = ref(new Map()) // tutorialId -> { element, config }
const tutorialGroups = ref(new Map()) // groupId -> [config, ...]
const activeTutorialId = ref(null)
const activeStepIndex = ref(0)
const introVisible = ref(false)
const isTransitioning = ref(false)
const positionKey = ref(0)
const currentUserId = ref(null)

const autoTriggerQueue = [] // tutorial ids waiting for the current tour to end
const autoTriggeredThisSession = new Set()
let introTimer = null
let pendingStartId = null // step waiting behind the intro splash

function isCartCurrentlyOpen() {
  try {
    return useCartStore().isCartOpen
  } catch {
    return false // Pinia not ready yet (app bootstrap)
  }
}

function isAllowedNow(group) {
  return !isCartCurrentlyOpen() || group === CART_TUTORIAL_GROUP
}

function sortedGroupSteps(groupId) {
  const group = tutorialGroups.value.get(groupId)
  if (!group?.length) return []
  return [...group].sort((a, b) => (a.step || 0) - (b.step || 0))
}

/**
 * Scroll an element into view (if needed), then resolve
 */
function scrollElementIntoView(element) {
  return new Promise((resolve) => {
    if (!element) return resolve()
    const rect = element.getBoundingClientRect()
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return resolve()
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    setTimeout(resolve, 350)
  })
}

export function useTutorial() {
  const activeTutorial = computed(
    () => registeredElements.value.get(activeTutorialId.value) || null
  )
  const activeElement = computed(() => activeTutorial.value?.element || null)
  const activeConfig = computed(() => activeTutorial.value?.config || null)
  const isActive = computed(() => activeTutorialId.value !== null && activeElement.value !== null)

  // All steps of the active group (single tips yield a one-item array)
  const currentGroupSteps = computed(() => {
    const config = activeConfig.value
    if (!config) return []
    if (!config.group) return [config]
    const steps = sortedGroupSteps(config.group)
    return steps.length ? steps : [config]
  })

  const stepInfo = computed(() => {
    const total = currentGroupSteps.value.length
    const current = activeStepIndex.value + 1
    return {
      current,
      total,
      isFirst: current === 1,
      isLast: current === total,
      hasMultipleSteps: total > 1,
    }
  })

  function setUserId(userId) {
    currentUserId.value = userId
  }

  // ============================================
  // Registration (called by the v-tutorial directive)
  // ============================================
  function registerElement(tutorialId, element, config) {
    const fullConfig = {
      id: tutorialId,
      text: '',
      importance: 'info',
      showOnce: true,
      autoTrigger: false,
      step: 1,
      group: null,
      intro: true, // grouped tours open with the fullscreen splash unless intro:false
      ...config,
    }

    registeredElements.value.set(tutorialId, { element, config: fullConfig })

    if (fullConfig.group) {
      if (!tutorialGroups.value.has(fullConfig.group)) {
        tutorialGroups.value.set(fullConfig.group, [])
      }
      const group = tutorialGroups.value.get(fullConfig.group)
      const existingIndex = group.findIndex((t) => t.id === tutorialId)
      if (existingIndex >= 0) group[existingIndex] = fullConfig
      else group.push(fullConfig)
    }

    // Auto-trigger once per session, once per user
    if (fullConfig.autoTrigger && !autoTriggeredThisSession.has(tutorialId)) {
      if (!isAllowedNow(fullConfig.group)) return
      const alreadySeen = fullConfig.showOnce && isSeenByUser(currentUserId.value, tutorialId)
      if (alreadySeen) return

      autoTriggeredThisSession.add(tutorialId)
      if (!activeTutorialId.value && !introVisible.value) {
        startTutorial(tutorialId)
      } else if (!autoTriggerQueue.includes(tutorialId)) {
        autoTriggerQueue.push(tutorialId)
      }
    }
  }

  function unregisterElement(tutorialId) {
    registeredElements.value.delete(tutorialId)

    const queueIndex = autoTriggerQueue.indexOf(tutorialId)
    if (queueIndex >= 0) autoTriggerQueue.splice(queueIndex, 1)

    // Remove from its group so step counts stay accurate
    for (const [groupId, group] of tutorialGroups.value) {
      const idx = group.findIndex((t) => t.id === tutorialId)
      if (idx >= 0) {
        group.splice(idx, 1)
        if (group.length === 0) tutorialGroups.value.delete(groupId)
        break
      }
    }

    if (pendingStartId === tutorialId) cancelIntro()
    if (activeTutorialId.value === tutorialId) endTutorial()
  }

  // ============================================
  // Tour lifecycle
  // ============================================

  /**
   * Start a tutorial. Grouped tours open with the fullscreen intro splash
   * and begin from their first step; single tips show immediately.
   * @returns {boolean} whether the tutorial started
   */
  function startTutorial(tutorialId) {
    const tutorial = registeredElements.value.get(tutorialId)
    if (!tutorial) return false
    if (!isAllowedNow(tutorial.config.group)) return false
    if (tutorial.config.showOnce && isSeenByUser(currentUserId.value, tutorialId)) return false

    // Grouped tours always begin from their first registered step
    let entryId = tutorialId
    if (tutorial.config.group) {
      const first = sortedGroupSteps(tutorial.config.group).find((s) =>
        registeredElements.value.has(s.id)
      )
      if (first) entryId = first.id
    }

    if (tutorial.config.group && tutorial.config.intro !== false) {
      showIntroThenStart(entryId)
    } else {
      activateStep(entryId, 0)
    }
    return true
  }

  /**
   * Start a multi-step tour by its group id
   */
  function startTutorialGroup(groupId) {
    const first = sortedGroupSteps(groupId).find((s) => registeredElements.value.has(s.id))
    if (!first) return false
    return startTutorial(first.id)
  }

  function showIntroThenStart(stepId) {
    cancelIntro()
    pendingStartId = stepId
    introVisible.value = true
    introTimer = setTimeout(dismissIntro, INTRO_SPLASH_MS)
  }

  /**
   * Close the intro splash and begin the pending tour (auto-timer or tap)
   */
  function dismissIntro() {
    const stepId = pendingStartId
    cancelIntro()
    if (stepId && registeredElements.value.has(stepId)) {
      activateStep(stepId, 0)
    }
  }

  /**
   * Leave guide mode from the intro splash - marks the pending tour as seen
   */
  function skipAllFromIntro() {
    const stepId = pendingStartId
    cancelIntro()
    const config = registeredElements.value.get(stepId)?.config
    if (config) markTourSeen(config)
    processQueue()
  }

  function cancelIntro() {
    if (introTimer) {
      clearTimeout(introTimer)
      introTimer = null
    }
    introVisible.value = false
    pendingStartId = null
  }

  function activateStep(stepId, index) {
    const element = registeredElements.value.get(stepId)?.element
    scrollElementIntoView(element).then(() => {
      activeTutorialId.value = stepId
      activeStepIndex.value = index
      isTransitioning.value = false
    })
  }

  /**
   * Advance to the next step; finishing the last step completes the tour
   */
  function nextStep() {
    const config = activeConfig.value
    if (!config) return

    const steps = currentGroupSteps.value
    const nextIndex = activeStepIndex.value + 1

    if (!config.group || nextIndex >= steps.length) {
      finishTour()
      return
    }

    const next = steps[nextIndex]
    if (next && registeredElements.value.has(next.id)) {
      isTransitioning.value = true
      activateStep(next.id, nextIndex)
    } else {
      finishTour()
    }
  }

  /**
   * Leave guide mode entirely - marks the whole tour as seen
   */
  function skipAll() {
    finishTour()
  }

  /**
   * Complete the tour: mark all its steps seen, close, process the queue
   */
  function finishTour() {
    const config = activeConfig.value
    if (config) markTourSeen(config)
    endTutorial()
  }

  function markTourSeen(config) {
    if (config.showOnce) markSeenByUser(currentUserId.value, config.id)
    if (config.group) {
      for (const stepConfig of tutorialGroups.value.get(config.group) || []) {
        if (stepConfig.showOnce) markSeenByUser(currentUserId.value, stepConfig.id)
      }
    }
  }

  /**
   * Close the tour without marking anything as seen
   */
  function endTutorial() {
    activeTutorialId.value = null
    activeStepIndex.value = 0
    isTransitioning.value = false
    processQueue()
  }

  function processQueue() {
    while (autoTriggerQueue.length > 0) {
      const nextId = autoTriggerQueue.shift()
      const tutorial = registeredElements.value.get(nextId)
      if (!tutorial) continue
      const seen = tutorial.config.showOnce && isSeenByUser(currentUserId.value, nextId)
      if (!seen) {
        setTimeout(() => startTutorial(nextId), 300)
        break
      }
    }
  }

  /**
   * Force position refresh (window resize)
   */
  function refreshPosition() {
    positionKey.value++
  }

  return {
    // State (readonly)
    isActive: readonly(isActive),
    activeElement: readonly(activeElement),
    activeConfig: readonly(activeConfig),
    activeTutorialId: readonly(activeTutorialId),
    stepInfo: readonly(stepInfo),
    isTransitioning: readonly(isTransitioning),
    introVisible: readonly(introVisible),
    positionKey: readonly(positionKey),

    // Constants
    IMPORTANCE_LEVELS,

    // Methods
    setUserId,
    registerElement,
    unregisterElement,
    startTutorial,
    startTutorialGroup,
    dismissIntro,
    skipAllFromIntro,
    nextStep,
    skipAll,
    endTutorial,
    refreshPosition,
  }
}
